import {
  faBook,
  faChalkboardUser,
  faQuestion,
  faStar,
  faThumbsDown,
  faThumbsUp,
  type IconDefinition,
} from '@fortawesome/pro-regular-svg-icons'
import { IANAZone } from 'luxon'

import type { UserContextType } from '@/client/context/user'
import type { SerializedRichText } from '@/client/services/richText/types'
import type { PostFeedbackRating, QuestionType } from '@/client/services/worlds'

export const RATING_TYPE_TO_ICON: Record<PostFeedbackRating, IconDefinition> = {
  'Super Helpful': faStar,
  Helpful: faThumbsUp,
  'Not Helpful': faThumbsDown,
}

export const SHOULD_REQUEST_LIVE_HELP_DEFAULT = true

export const QUESTION_TYPE_TO_RICH_TEXT_TEMPLATE: Record<
  QuestionType,
  (user: UserContextType) => SerializedRichText
> = {
  'problem-help': () => ({
    content: [
      {
        type: 'template',
        readOnlyText: 'Question:',
        children: [
          {
            text: 'Tutors will help give hints to get you on track. Please share your problem (type or upload a photo)!',
            bold: true,
          },
        ],
      },
      {
        type: 'paragraph',
        placeholder: `I'm trying to solve 4x+2=3 for x`,
        children: [{ bold: false, text: '' }],
      },
      { type: 'paragraph', children: [{ bold: false, text: '' }] },
      {
        type: 'template',
        readOnlyText: 'So far:',
        children: [{ bold: true, text: 'What have you tried so far, and where are you stuck?' }],
      },
      {
        type: 'paragraph',
        placeholder: `I tried subtracting 2 but I'm not sure what to do next...`,
        children: [{ bold: false, text: '' }],
      },
      { type: 'paragraph', children: [{ bold: false, text: '' }] },
    ],
    serialization_method: 'slate_json_v1',
  }),
  'assignment-help': () => ({
    content: [
      {
        type: 'template',
        readOnlyText: 'Question:',
        children: [
          {
            text: 'Tutors will help you with 1-2 questions to help jumpstart your learning. Please type out one example question to help tutors better understand your assignment.',
            bold: true,
          },
        ],
      },
      {
        type: 'paragraph',
        placeholder: `My homework is about systems of equations. Here's a question I'm stuck on...`,
        children: [{ bold: false, text: '' }],
      },
      { type: 'paragraph', children: [{ bold: false, text: '' }] },
      {
        type: 'template',
        readOnlyText: 'So far:',
        children: [{ bold: true, text: 'What have you tried so far, and where are you stuck?' }],
      },
      {
        type: 'paragraph',
        placeholder: `I tried combining like terms, but I'm not sure what to do next...`,
        children: [{ bold: false, text: '' }],
      },
      { type: 'paragraph', children: [{ bold: false, text: '' }] },
    ],
    serialization_method: 'slate_json_v1',
  }),
  'topic-review': user => {
    const tz = new IANAZone(user.timezone)
    const tzInits = tz.offsetName(Date.now(), { format: 'short' })
    return {
      content: [
        {
          type: 'template',
          readOnlyText: 'Topic:',
          children: [{ text: 'What would you like to learn / review?', bold: true }],
        },
        {
          type: 'paragraph',
          placeholder: 'I want to review fraction addition because I have a test next week...',
          children: [{ bold: false, text: '' }],
        },
        { type: 'paragraph', children: [{ bold: false, text: '' }] },
        {
          type: 'template',
          readOnlyText: `Availability (in ${tzInits}):`,
          children: [
            {
              bold: true,
              text: `Tutors will schedule a session to go over this topic. When are you free over the next week (in ${tzInits})?`,
            },
          ],
        },
        {
          type: 'paragraph',
          placeholder: `I'm free between 9 and 11 AM on Tuesday, Thursday, and Friday.`,
          children: [{ bold: false, text: '' }],
        },
        { type: 'paragraph', children: [{ bold: false, text: '' }] },
      ],
      serialization_method: 'slate_json_v1',
    }
  },
}

export const QUESTION_TYPE_TO_LABEL: Record<QuestionType, string> = {
  'problem-help': 'I have a quick question about a problem (<10 minutes)',
  'assignment-help': 'I need help with an assignment (10-30 minutes)',
  'topic-review': 'I want to review a topic / learn a new topic (30-90 minutes)',
}

export const QUESTION_TYPE_TO_ICON: Record<QuestionType, IconDefinition> = {
  'problem-help': faQuestion,
  'assignment-help': faBook,
  'topic-review': faChalkboardUser,
}
